// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import styles from '@app/features/app/components/dialogs/components/plutonium/PurchaseHistorySection.module.css';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {getFormattedShortDate} from '@app/vendor/date_utils/DateFormatting';
import type {SelfServeRefundEligibilityResponse} from '@app/vendor/schema/domains/premium/PremiumSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const logger = new Logger('SelfServeRefundSection');
function formatRelativeTime(isoTimestamp: string | null): string | null {
	if (!isoTimestamp) return null;
	const date = new Date(isoTimestamp);
	if (Number.isNaN(date.getTime())) return null;
	return getFormattedShortDate(date, getCurrentLocale());
}
export const SelfServeRefundSection: React.FC = observer(() => {
	const {t} = useLingui();
	const [eligibility, setEligibility] = useState<SelfServeRefundEligibilityResponse | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const refresh = useCallback(async () => {
		try {
			const result = await PremiumCommands.fetchSelfServeRefundEligibility();
			setEligibility(result);
		} catch (error) {
			logger.error('Failed to fetch refund eligibility', error);
			setEligibility(null);
		}
	}, []);
	useEffect(() => {
		void refresh();
	}, [refresh]);
	const handleRefund = useCallback(() => {
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Refund your most recent purchase?`}
					description={
						<Trans>
							This refunds your most recent paid invoice and, if it was tied to a subscription, cancels that
							subscription immediately. You won't be able to self-serve refund again for 30 days. This can't be undone.
						</Trans>
					}
					primaryText={t`Refund Purchase`}
					primaryVariant="danger-primary"
					secondaryText={t`Not Now`}
					onPrimary={async () => {
						setSubmitting(true);
						try {
							await PremiumCommands.refundLatestPurchase();
							ToastCommands.success(t`Refund issued. It may take a few business days to appear on your statement.`);
							await refresh();
						} catch (error) {
							logger.error('Self-serve refund failed', error);
							ToastCommands.error(t`We couldn't refund this purchase. Please contact support if this keeps happening.`);
						} finally {
							setSubmitting(false);
						}
					}}
				/>
			)),
		);
	}, [refresh, t]);
	if (!eligibility || eligibility.reason === 'no_refundable_purchase' || eligibility.reason === 'feature_unavailable') {
		return null;
	}
	const isEligible = eligibility.eligible;
	const windowEndsOn = formatRelativeTime(eligibility.refund_window_expires_at);
	const cooldownEndsOn = formatRelativeTime(eligibility.cooldown_expires_at);
	return (
		<section className={styles.section}>
			<div className={styles.card}>
				<div className={styles.grid}>
					<div className={styles.content}>
						<h3 className={styles.title}>
							<Trans>Self-serve refund</Trans>
						</h3>
						<p className={styles.description}>
							{isEligible ? (
								eligibility.cancels_subscription ? (
									<Trans>
										You can refund your most recent purchase until {windowEndsOn}. This will also cancel your
										subscription immediately.
									</Trans>
								) : (
									<Trans>You can refund your most recent purchase until {windowEndsOn}.</Trans>
								)
							) : eligibility.reason === 'outside_refund_window' ? (
								<Trans>
									Your last purchase is outside the 3-day self-serve refund window. Contact support if you have a
									billing issue.
								</Trans>
							) : eligibility.reason === 'cooldown_active' ? (
								<Trans>
									You can self-serve refund again on {cooldownEndsOn}. Contact support if you have a billing issue.
								</Trans>
							) : (
								<Trans>This purchase isn't eligible for a self-serve refund.</Trans>
							)}
						</p>
					</div>
					<Button
						variant="danger-primary"
						onClick={handleRefund}
						submitting={submitting}
						disabled={!isEligible}
						className={styles.button}
					>
						<Trans>Refund Purchase</Trans>
					</Button>
				</div>
			</div>
		</section>
	);
});
